import logger from './Logger';

const COMPONENT = 'ReprocessUtils';

/**
 * Build an HTML string that shows corrected text with words that differ
 * from original highlighted in specified color. Uses LCS-based word diff.
 * Returns escaped HTML safe to embed inside transcript renderer.
 *
 * @param {string} original - Original text from transcription
 * @param {string} corrected - Corrected text from API
 * @param {string} color - Color for highlighted words (default: '#0066cc' for blue)
 * @returns {string} HTML string with highlighted corrections
 */
export function buildCorrectedHighlightHtml(original, corrected, color = '#0066cc') {
  const escape = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const orig = String(original || '').trim().split(/\s+/).filter(Boolean);
  const corr = String(corrected || '').trim().split(/\s+/).filter(Boolean);

  if (corr.length === 0) return '';
  if (orig.length === 0) {
    return corr.map(w => `<span style="color:${color};font-weight:500">${escape(w)}</span>`).join(' ');
  }

  const m = orig.length;
  const n = corr.length;
  // LCS table (m+1) x (n+1)
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (orig[i] === corr[j]) {
        dp[i][j] = dp[i + 1][j + 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  let i = 0;
  let j = 0;
  const out = [];
  while (j < n) {
    if (i < m && orig[i] === corr[j]) {
      out.push(escape(corr[j]));
      i++;
      j++;
    } else if (i < m && dp[i + 1][j] >= dp[i][j + 1]) {
      // word removed/changed from original — skip it
      i++;
    } else {
      // word added/changed in corrected — highlight in blue
      out.push(`<span style="color:${color};font-weight:500">${escape(corr[j])}</span>`);
      j++;
    }
  }
  return out.join(' ');
}

/**
 * Call the /reprocess API endpoint to get corrected text for a specific segment
 * and apply word-level corrections to the transcript, highlighting changed words in blue.
 *
 * Enhanced: when the API returns `prev_div_corrected` + `prev_div_id` (new-division
 * processing), the full `data` object is returned to the caller so that
 * `sentenceBoundaryUtils.applyPrevDivCorrection` can overwrite the previous
 * paragraph in the UI.  The existing current-segment correction flow is unchanged.
 *
 * @param {Object} params - Parameters object
 * @param {string} params.conversationId - Conversation ID
 * @param {string} params.sessionId - Session ID
 * @param {string} params.divId - Paragraph identifier (div_id)
 * @param {number} params.sequenceId - Word sequence ID within paragraph
 * @param {string} params.originalText - Original transcribed text
 * @param {string} params.baseUrl - API base URL
 * @param {string} params.reprocessEndpoint - Reprocess endpoint path
 * @param {string} params.authIdToken - Authorization token
 * @param {Function} params.setTranscriptSegments - State setter for transcript segments
 * @param {Function} params.setPersistentSegments - State setter for persistent segments
 * @param {Function} params.sendSegmentToViewers - Function to send corrected segment to viewers
 * @param {Object} params.viewerDeps - Dependencies for sendSegmentToViewers (socketRef, isPausedRef, etc.)
 * @param {string} params.highlightColor - Color for highlighting corrections (default: blue)
 * @returns {Promise<Object|null>} Full API response data (may include prev_div_corrected), or null if failed
 */
export async function reprocessMessage({
  conversationId,
  sessionId,
  divId,
  sequenceId,
  originalText,
  baseUrl,
  reprocessEndpoint,
  authIdToken,
  setTranscriptSegments,
  setPersistentSegments,
  sendSegmentToViewers,
  viewerDeps,
  highlightColor = '#0066cc', // Blue color for web
}) {
  // Validate required fields
  if (!conversationId || !sessionId || !divId || typeof sequenceId !== 'number') {
    logger.warn('reprocessMessage called with missing required fields', {
      component: COMPONENT,
      action: 'REPROCESS_MISSING_FIELDS',
      context: {
        hasConversationId: !!conversationId,
        hasSessionId: !!sessionId,
        hasDivId: !!divId,
        sequenceIdType: typeof sequenceId,
      },
    });
    return null;
  }

  if (!baseUrl || !reprocessEndpoint || !authIdToken) {
    logger.warn('reprocessMessage called with missing API configuration', {
      component: COMPONENT,
      action: 'REPROCESS_MISSING_CONFIG',
      context: {
        hasBaseUrl: !!baseUrl,
        hasEndpoint: !!reprocessEndpoint,
        hasAuthToken: !!authIdToken,
      },
    });
    return null;
  }

  const restoreOriginalText = () => {
    const updater = (segments) => {
      if (!Array.isArray(segments)) return segments;
      let changed = false;
      const next = segments.map(seg => {
        if (
          seg &&
          seg.div_id === divId &&
          seg.sequence_id === sequenceId &&
          (seg.type === 'text' || seg.type === undefined) &&
          seg.final !== false &&
          seg.text !== originalText
        ) {
          changed = true;
          return { ...seg, text: originalText, corrected: false };
        }
        return seg;
      });
      return changed ? next : segments;
    };
    setTranscriptSegments(prev => updater(prev));
    setPersistentSegments(prev => updater(prev));
  };

  const reqPayload = {
    conversation_id: conversationId,
    session_id: sessionId,
    div_id: divId,
    sequence_id: sequenceId,
  };

  const reprocessUrl = `${baseUrl}${reprocessEndpoint}`;

  try {
    const response = await fetch(reprocessUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authIdToken,
      },
      body: JSON.stringify(reqPayload),
    });

    const rawText = await response.text();
    let data = null;

    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch (parseErr) {
      logger.warn('Failed to parse reprocess API response', {
        component: COMPONENT,
        action: 'REPROCESS_PARSE_ERROR',
        context: {
          status: response.status,
          rawTextLength: rawText?.length || 0,
        },
        error: parseErr,
      });
    }

    // Check if response was not successful
    if (!response.ok) {
      logger.error('Reprocess API returned error status', {
        component: COMPONENT,
        action: 'REPROCESS_FAILED',
        context: {
          status: response.status,
          statusText: response.statusText,
          responseBody: rawText,
        },
      });
      console.error(`[REPROCESS] API failed with status ${response.status}`);
      restoreOriginalText();
      return null;
    }

    // Check if status is not success
    if (!data || data.status !== 'success') {
      logger.debug('Reprocess returned non-success status', {
        component: COMPONENT,
        action: 'REPROCESS_NOT_SUCCESS',
        context: {
          returnedStatus: data?.status,
          reason: data?.message || 'unknown',
        },
      });
      restoreOriginalText();
      return null;
    }

    // ─── Sentence boundary fields ────────────────────────────────────────────
    // If the API returned prev_div_corrected + prev_div_id we must return the
    // full data object to the caller (Record.jsx) so it can invoke
    // applyPrevDivCorrection().  We do NOT handle those fields here — that is
    // the responsibility of sentenceBoundaryUtils — keeping concerns separated.
    const hasPrevDivData =
      typeof data.prev_div_corrected === 'string' && data.prev_div_corrected.trim() &&
      typeof data.prev_div_id === 'string' && data.prev_div_id.trim();
    // ─────────────────────────────────────────────────────────────────────────
    // Extract and validate corrected text for the CURRENT segment
    const corrected = typeof data.corrected === 'string' ? data.corrected : '';
    const original = typeof data.original === 'string' ? data.original : (originalText || '');
    const modelFixes = typeof data.model_fixes === 'number' ? data.model_fixes : 0;

    // Skip current-segment UI update if no corrections were made, but still
    // return data so the caller can handle prev_div_corrected if present.
    if (!corrected || modelFixes <= 0 || corrected.trim() === original.trim()) {
      logger.debug('No corrections found to apply for current segment', {
        component: COMPONENT,
        action: 'REPROCESS_NO_CHANGES',
        context: {
          modelFixes,
          correctedLength: corrected?.length || 0,
          originalLength: original?.length || 0,
          hasPrevDivData,
        },
      });
      // Return data anyway so caller can use prev_div_corrected if present
      return hasPrevDivData ? data : null; 
    }

    // Build highlighted HTML with corrected text
    const highlightedHtml = buildCorrectedHighlightHtml(original, corrected, highlightColor);

    logger.info('Applying reprocessed corrections to transcript', {
      component: COMPONENT,
      action: 'REPROCESS_APPLY_CORRECTIONS',
      context: {
        divId,
        sequenceId,
        modelFixes,
        highlightedHtmlLength: highlightedHtml?.length || 0,
        color: highlightColor,
      },
    });

    // Update segments with corrected text
    const updater = (segments) => {
      if (!Array.isArray(segments)) return segments;

      let changed = false;
      const next = segments.map(seg => {
        if (
          seg &&
          seg.div_id === divId &&
          seg.sequence_id === sequenceId &&
          (seg.type === 'text' || seg.type === undefined) &&
          seg.final !== false
        ) {
          changed = true;
          return {
            ...seg,
            text: highlightedHtml,
            corrected: true,
            originalText: seg.text,
            modelFixes: modelFixes,
          };
        }
        return seg;
      });

      return changed ? next : segments;
    };

    // Apply updates to both transcript sources
    setTranscriptSegments(prev => updater(prev));
    setPersistentSegments(prev => updater(prev));

    // Send corrected text to viewers with is_corrected flag
    if (sendSegmentToViewers && viewerDeps) {
      try {
        sendSegmentToViewers(
          highlightedHtml,
          false, // isPartial
          'segment', // actionType
          divId,
          sequenceId,
          new Date().toISOString().slice(0, 19), // create_timestamp
          viewerDeps,
          true
        );
        logger.info('Sent corrected text to viewers', {
          component: COMPONENT,
          action: 'REPROCESS_SENT_TO_VIEWERS',
          context: {
            divId,
            sequenceId,
            modelFixes,
          },
        });
      } catch (viewerErr) {
        logger.warn('Failed to send corrected text to viewers', {
          component: COMPONENT,
          action: 'REPROCESS_VIEWER_SEND_FAILED',
          context: {
            divId,
            sequenceId,
          },
          error: viewerErr,
        });
      }
    }

    logger.info('Reprocess corrections applied successfully', {
      component: COMPONENT,
      action: 'REPROCESS_SUCCESS',
      context: {
        divId,
        sequenceId,
        modelFixes,
        hasPrevDivData,
      },
    });

    // Always return the full data object so the caller can handle
    // prev_div_corrected / prev_div_id via sentenceBoundaryUtils
    return data;

  } catch (error) {
    logger.error('Reprocess API call failed with exception', {
      component: COMPONENT,
      action: 'REPROCESS_ERROR',
      context: {
        conversationId,
        sessionId,
        divId,
        sequenceId,
      },
      error,
    });

    console.error('[REPROCESS] Error:', error);
    restoreOriginalText();
    return null;
  }
}

/**
 * Load reprocess preference from localStorage
 * @returns {Promise<boolean>} True if reprocess is enabled
 */
export async function loadReprocessPreference() {
  try {
    const stored = localStorage.getItem('reprocess_enabled');
    const enabled = stored === 'true';
    logger.debug('Loaded reprocess preference from localStorage', {
      component: COMPONENT,
      action: 'LOAD_PREFERENCE',
      context: { enabled },
    });
    return enabled;
  } catch (e) {
    logger.warn('Failed to load reprocess preference from localStorage', {
      component: COMPONENT,
      action: 'LOAD_PREFERENCE_FAILED',
      error: e,
    });
    console.warn('[Reprocess] Failed to load preference:', e?.message || e);
    return false;
  }
}

/**
 * Save reprocess preference to localStorage
 * @param {boolean} enabled - Whether reprocess should be enabled
 * @returns {Promise<void>}
 */
export async function saveReprocessPreference(enabled) {
  try {
    localStorage.setItem('reprocess_enabled', enabled ? 'true' : 'false');
    logger.debug('Saved reprocess preference to localStorage', {
      component: COMPONENT,
      action: 'SAVE_PREFERENCE',
      context: { enabled },
    });
  } catch (e) {
    logger.warn('Failed to save reprocess preference to localStorage', {
      component: COMPONENT,
      action: 'SAVE_PREFERENCE_FAILED',
      error: e,
    });
    }
}

/**
 * Clear reprocess preference from localStorage
 * @returns {Promise<void>}
 */
export async function clearReprocessPreference() {
  try {
    localStorage.removeItem('reprocess_enabled');
    logger.debug('Cleared reprocess preference from localStorage', {
      component: COMPONENT,
      action: 'CLEAR_PREFERENCE',
    });
  } catch (e) {
    logger.warn('Failed to clear reprocess preference', {
      component: COMPONENT,
      action: 'CLEAR_PREFERENCE_FAILED',
      error: e,
    });
    console.warn('[Reprocess] Failed to clear preference:', e?.message || e);
  }
}